import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

/** ---------- Toast (no external lib) ---------- */
function ToastStack({ toasts, onRemove }) {
  const stackRef = useRef(null);

  useEffect(() => {
    if (!stackRef.current) return;
    gsap.fromTo(
      stackRef.current.querySelectorAll(".toast-item"),
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.2, stagger: 0.03, ease: "power2.out" }
    );
  }, [toasts]);

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[9999] w-[min(92vw,380px)]">
      <div ref={stackRef} className="flex flex-col gap-2">
        {toasts.map((t) => {
          const styles =
            t.type === "success"
              ? "border-green-200 bg-green-50 text-green-900"
              : t.type === "error"
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-[#d4af37]/30 bg-[#fff7e6] text-[#7d5a19]";

          return (
            <div
              key={t.id}
              className={[
                "toast-item pointer-events-auto rounded-xl border px-4 py-3 shadow-sm",
                styles,
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm">
                  {t.title && <div className="font-semibold">{t.title}</div>}
                  <div className="mt-0.5">{t.message}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(t.id)}
                  className="rounded-lg border border-black/10 bg-white/70 px-2 py-1 text-xs font-semibold transition hover:bg-white"
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AddProductSize() {
  const [categories, setCategories] = useState([]);

  const [categoryId, setCategoryId] = useState("");
  const [categorySizeGroupId, setCategorySizeGroupId] = useState("");
  const [size, setSize] = useState("");
  const [customSize, setCustomSize] = useState("");

  const [categoryName, setCategoryName] = useState("");

  const [loadingCats, setLoadingCats] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Toasts
  const [toasts, setToasts] = useState([]);
  const wrapRef = useRef(null);

  const toast = (type, message, title) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((prev) => [...prev, { id, type, message, title }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 2800);
  };

  const removeToast = (id) =>
    setToasts((prev) => prev.filter((t) => t.id !== id));

  // GSAP entrance
  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".aps-anim",
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.45, stagger: 0.06, ease: "power2.out" }
      );
    }, wrapRef);

    return () => ctx.revert();
  }, []);

  // Fetch main categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCats(true);
        const response = await fetch(`${ApiURl}/getMainCategory.php`);
        const data = await response.json();

        const list = Array.isArray(data?.data) ? data.data : [];
        setCategories(list);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
        toast("error", "Failed to load categories.");
      } finally {
        setLoadingCats(false);
      }
    };

    fetchCategories();
  }, []);

  // Handle category change
  const handleCategoryChange = (e) => {
    const selectedId = e.target.value;
    setCategoryId(selectedId);
    
    const category = categories.find((c) => c.id == selectedId);
    setCategoryName(category?.category_name || "");
    setCategorySizeGroupId(selectedId);
  };

  // Size options
  const SIZE_OPTIONS = ["Small", "Medium", "Large", "XL", "XXL", "Custom"];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Determine the actual size value
    let actualSize = size;
    if (size === "Custom" && customSize.trim()) {
      actualSize = customSize.trim();
    }

    // Validation
    if (!categoryId) {
      toast("error", "Please select a category.");
      return;
    }
    
    if (!actualSize) {
      toast("error", "Please select or enter a size.");
      return;
    }

    setSubmitting(true);
    toast("info", "Adding size...");

    try {
      const payload = {
        main_category_id: categoryId,
        // Kept for the existing PHP endpoint; UI now treats category as the only grouping.
        sub_category_id: categorySizeGroupId,
        size: actualSize
      };

      console.log("Sending payload:", payload);

      const response = await fetch(`${ApiURl}/addSize.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      console.log("Raw response:", responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("JSON parse error:", parseError, "Response was:", responseText);
        toast("error", "Invalid response from server.");
        return;
      }
      
      console.log("Parsed response data:", data);

      if (data.status === "success") {
        toast("success", data.message || "Size added successfully!");
        // Reset form
        setSize("");
        setCustomSize("");
      } else {
        toast("error", data.message || "Error adding size.");
      }
    } catch (error) {
      console.error("Network error:", error);
      toast("error", `Network error: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle size selection
  const handleSizeChange = (e) => {
    const value = e.target.value;
    setSize(value);
    if (value !== "Custom") {
      setCustomSize("");
    }
  };

  return (
    <div ref={wrapRef} className="add-product-size-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
      <ToastStack toasts={toasts} onRemove={removeToast} />

      <div className="aps-anim mx-auto max-w-[1200px] rounded-[26px] border border-[#d8c6b7] bg-[#fffaf5] p-4 shadow-[0_22px_60px_rgba(43,0,0,0.08)] sm:p-6 lg:p-8">
        <div className="aps-anim mb-7 rounded-[20px] border border-[#e3d6cb] bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.12),transparent_34%),linear-gradient(135deg,#fffaf5,#f2e6dc)] p-5 sm:p-6">
          <h2 className="font-serif text-3xl font-medium tracking-tight text-[#2B0000] sm:text-4xl">
            Add Product Size
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[#6f5e57]">
            Select a category, then add the size.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="aps-anim">
          <div className="grid grid-cols-1 gap-6">
            {/* Category */}
            <div className="rounded-[22px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f3e8dd)] p-4 shadow-[0_16px_42px_rgba(43,0,0,0.06)] sm:p-6">
              <label className="mb-2 block text-sm font-semibold text-[#4a352f]">
                Category
              </label>
              <select
                className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                onChange={handleCategoryChange}
                value={categoryId}
                disabled={loadingCats}
              >
                <option value="">
                  {loadingCats ? "Loading categories..." : "Select category"}
                </option>
                {categories.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.category_name}
                  </option>
                ))}
              </select>
              {categoryName && (
                <p className="mt-2 rounded-lg border border-[#eadfd5] bg-white/70 px-3 py-2 text-xs text-[#7d6c64]">
                  Selected: <span className="font-medium">{categoryName}</span>
                </p>
              )}
            </div>

            {/* Size Section - Full width below */}
            <div className="rounded-[22px] border border-[#decfc3] bg-white p-4 shadow-[0_16px_42px_rgba(43,0,0,0.06)] sm:p-6">
              <label className="mb-2 block text-sm font-semibold text-[#4a352f]">
                Size
              </label>

              {/* Size dropdown */}
              <select
                value={size}
                onChange={handleSizeChange}
                className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                required
              >
                <option value="">Select size</option>
                {SIZE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              {/* Custom size input (shown when "Custom" is selected) */}
              {size === "Custom" && (
                <div className="mt-4">
                  <label className="mb-2 block text-sm font-medium text-[#4a352f]">
                    Enter Custom Size
                  </label>
                  <input
                    type="text"
                    value={customSize}
                    onChange={(e) => setCustomSize(e.target.value)}
                    className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                    placeholder="e.g., 10x12 inches, 500ml, etc."
                    required={size === "Custom"}
                  />
                  <p className="mt-2 text-xs text-[#7d6c64]">
                    Enter the custom size value
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex gap-3">
                <button
                  type="submit"
                  disabled={submitting || !categoryId || !categorySizeGroupId}
                  className={[
                    "inline-flex flex-1 items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold transition shadow-[0_12px_28px_rgba(43,0,0,0.16)]",
                    submitting || !categoryId || !categorySizeGroupId
                      ? "cursor-not-allowed bg-[#b7a69b] text-white"
                      : "bg-[#2B0000] text-[#f4dfae] hover:-translate-y-0.5 hover:bg-[#4A090B] active:translate-y-[1px]",
                  ].join(" ")}
                >
                  {submitting ? (
                    <>
                      <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Adding...
                    </>
                  ) : (
                    "Add Size"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setCategoryId("");
                    setCategoryName("");
                    setCategorySizeGroupId("");
                    setSize("");
                    setCustomSize("");
                    toast("info", "Form cleared.");
                  }}
                  className="rounded-xl border border-[#d6c4b5] bg-white px-5 py-3 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
                  disabled={submitting}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Summary Section */}
        {categoryName && (
          <div className="aps-anim mt-6 rounded-[20px] border border-[#d4af37]/22 bg-[linear-gradient(145deg,#fffaf5,#f2e6dc)] p-4 shadow-[0_12px_30px_rgba(43,0,0,0.05)] sm:p-5">
            <h3 className="mb-3 font-serif text-lg font-medium text-[#2B0000]">Current Selection Summary</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <span className="text-xs text-[#7d6c64]">Category</span>
                <p className="mt-1 text-sm font-semibold text-[#2B0000]">{categoryName || "—"}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .add-product-size-luxury {
          background:
            radial-gradient(circle at 7% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43,0,0,.04), transparent 30%),
            #F7EFE7;
        }

        .add-product-size-luxury input,
        .add-product-size-luxury select,
        .add-product-size-luxury button {
          font: inherit;
        }

        .add-product-size-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .add-product-size-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143,111,56,.5) rgba(212,175,55,.07);
        }

        .add-product-size-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .add-product-size-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .add-product-size-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143,111,56,.5);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .add-product-size-luxury {
            padding: 10px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .add-product-size-luxury *,
          .add-product-size-luxury *::before,
          .add-product-size-luxury *::after {
            transition-duration: .01ms !important;
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}

export default AddProductSize;